import React, { useState } from "react";

import type { ContactImportAction, ContactImportRowDTO } from "../../data/contactsApi";
import {
  ACTION_LABEL,
  PUSHED_ACTION_LABEL,
  ACTION_STYLE,
  ROW_STATUS_LABEL,
  ROW_STATUS_STYLE,
  cellText,
  fieldLabel,
  groupByAction,
} from "../../domain/contacts";
import { ChevronDownIcon, ChevronRightIcon } from "../../components/icons";
import { cn } from "../../components/cn";

/**
 * Every row of the sheet, grouped by what would happen to it.
 *
 * Grouped rather than left in sheet order, because a contact book is two
 * hundred rows of which a hundred and ninety are noise. Each row still leads
 * with its own line number so it can be found in Excel — the grouping is for
 * reading, the number is for fixing.
 *
 * Unchanged starts collapsed and the rest start open. Collapsing a problem
 * would be hiding it; collapsing the rows that do nothing is the whole point.
 *
 * Still grouped by ACTION after a push, not by outcome. The CA is reading this
 * against the sheet they edited, and re-sorting the list under them at the
 * moment they most want to check a specific line would break that. The outcome
 * rides on each row instead.
 */

function RowLine({ row, showOutcome }: { row: ContactImportRowDTO; showOutcome: boolean }) {
  const changes = Object.entries(row.changes ?? {});

  return (
    <li
      className={cn(
        "flex flex-col gap-1 rounded-md border p-3",
        // Only failures get visual weight, and only once they are real. Tinting
        // every created row green would make a successful push a wall of colour
        // with the two rows that need attention somewhere inside it.
        showOutcome && row.status === "failed" && "border-amber-500/40 bg-amber-500/5"
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          {/* The line in their own spreadsheet, so a fix is a click away in Excel. */}
          Row {row.sheet_row_no}
        </span>
        <span className="text-sm font-medium">{row.contact_name ?? "(no name)"}</span>
        {row.gst_no !== null && (
          <span className="font-mono text-xs text-muted-foreground">{row.gst_no}</span>
        )}
        {showOutcome && (
          <span
            className={cn(
              "ml-auto shrink-0 rounded-sm px-1.5 py-0.5 text-[11px] font-medium",
              ROW_STATUS_STYLE[row.status]
            )}
          >
            {ROW_STATUS_LABEL[row.status]}
          </span>
        )}
      </div>

      {changes.length > 0 && (
        <ul className="flex flex-col gap-0.5 pl-1">
          {changes.map(([field, change]) => (
            <li key={field} className="flex flex-wrap items-baseline gap-1.5 text-xs">
              <span className="text-muted-foreground">{fieldLabel(field)}</span>
              <span className="line-through opacity-60">{cellText(change.from)}</span>
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
              <span className="font-medium">{cellText(change.to)}</span>
            </li>
          ))}
        </ul>
      )}

      {row.issues.map((issue, index) => (
        <p
          key={index}
          className={cn(
            "text-xs",
            issue.severity === "error"
              ? "text-amber-700 dark:text-amber-400"
              : "text-muted-foreground"
          )}
        >
          <span className="font-medium">{issue.field}:</span> {issue.message}
        </p>
      ))}

      {/*
        Zoho's own words, passed through rather than reworded. "The contact name
        already exists" is a sentence a CA can act on; anything we substituted
        would say less and could be wrong about why.
      */}
      {showOutcome && row.error !== null && (
        <p className="text-xs text-amber-700 dark:text-amber-400">{row.error}</p>
      )}
    </li>
  );
}

function Group({
  action,
  rows,
  open,
  onToggle,
  showOutcomes,
}: {
  action: ContactImportAction;
  rows: ContactImportRowDTO[];
  open: boolean;
  onToggle: () => void;
  showOutcomes: boolean;
}) {
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-left text-sm font-medium"
      >
        <Chevron className="size-4 text-muted-foreground" />
        <span className={cn("rounded-sm px-1.5 py-0.5 text-xs font-medium", ACTION_STYLE[action])}>
          {showOutcomes ? PUSHED_ACTION_LABEL[action] : ACTION_LABEL[action]}
        </span>
        <span className="text-muted-foreground">{rows.length}</span>
      </button>

      {open && (
        <ul className="flex max-h-[28rem] flex-col gap-2 overflow-y-auto">
          {rows.map((row) => (
            <RowLine key={row.id} row={row} showOutcome={showOutcomes} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function ContactImportRows({
  rows,
  showOutcomes = false,
}: {
  rows: ContactImportRowDTO[];
  /** Off until a push has run: every row is `pending` before that, and saying so two hundred times is noise. */
  showOutcomes?: boolean;
}) {
  const [collapsed, setCollapsed] = useState<Set<ContactImportAction>>(
    () => new Set<ContactImportAction>(["unchanged"])
  );

  function toggle(action: ContactImportAction) {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-4 border-t pt-4">
      {groupByAction(rows).map((group) => (
        <Group
          key={group.action}
          action={group.action}
          rows={group.rows}
          open={!collapsed.has(group.action)}
          onToggle={() => toggle(group.action)}
          showOutcomes={showOutcomes}
        />
      ))}
    </div>
  );
}
